import { useDownloadDocumentsAsZip } from '../utils/useDownloadDocumentsAsZip';
import { styled, keyframes, Theme } from '@mui/material/styles';
import { Keyframes } from '@emotion/react';
import { Button } from '@mui/material';
import { Download as DownloadIcon } from '@mui/icons-material';
import { IDocument } from 'types';
import { useLocale } from '@koopajs/react';
import { theme } from './Core/theme';
import { Api } from '@koopajs/app';
import { useAppSnackbar } from 'components/hooks/useAppSnackbar';

interface IDownloadAllMeetingDocumentsButtonProps {
  meetingId: string;
  meetingTitle?: string;
}

export const DownloadAllMeetingDocumentsButton: React.FC<IDownloadAllMeetingDocumentsButtonProps> = ({
  meetingId,
  meetingTitle
}) => {
  // Animated DownloadIcon with gradient effect
  const getGradientAnimation = (theme: Theme): Keyframes => keyframes`
    0% {
      color: ${theme.palette.primary.main};
    }
    50% {
      color: ${theme.palette.secondary.main};
    }
    100% {
      color: ${theme.palette.primary.main};
    }
  `;

  const AnimatedDownloadIcon = styled(DownloadIcon)(({ theme }) => {
    const gradientAnimation = getGradientAnimation(theme);
    return {
      '&.animating': {
        animation: `${gradientAnimation} 2s infinite linear`
      }
    };
  });

  // Keep only: letters, numbers, hyphens, brackets, spaces, and accented characters
  const safeRegex = /[^A-Za-z0-9À-ÖØ-öø-ÿœŒ’\-\(\)\[\]\.\s]/g;
  const safeFolderName = `${meetingTitle
    ?.normalize('NFC')
    .replace(safeRegex, '_')
    .replace(/\s+/g, ' ')
    .trim()} documents`;

  const { t } = useLocale();
  const { triggerSnackbar } = useAppSnackbar();

  const { downloadDocumentsAsZip, isDownloading } = useDownloadDocumentsAsZip({
    folderName: safeFolderName ? safeFolderName : t('common:labelDocuments')
  });

  const handleZipAndDownloadDocs = async (): Promise<void> => {
    try {
      const response = await Api.client.get<{ resources: IDocument[] }>(`/meetings/${meetingId}/documents`);

      const documents = response.data.resources;

      const documentsWithoutLinks = documents.filter((doc) => doc.attachedFile.format !== 'url');

      await downloadDocumentsAsZip({ documents: documentsWithoutLinks });
    } catch (error) {
      triggerSnackbar({
        snackbarText: t('hooks.useDownloadDocumentsAsZip.snackbarDownloaDocumentsFail'),
        variant: 'error'
      });
    }
  };
  return (
    <Button
      onClick={handleZipAndDownloadDocs}
      variant="outlined"
      startIcon={isDownloading ? <AnimatedDownloadIcon className="animating" /> : <DownloadIcon />}
      disabled={isDownloading}
      data-cy="meeting-view_download-documents-button"
      sx={{
        '& .MuiButton-startIcon': {
          transition: 'all 0.3s ease'
        },
        '&.Mui-disabled': {
          backgroundColor: theme.palette.customGrey.main,
          color: 'inherit'
        }
      }}
    >
      {t('common:labelDownloadDocuments')}
    </Button>
  );
};
